import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {useMutation} from '@apollo/react-hooks';

import type {FilterObject} from 'nutshell-core/types';
import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {ButtonGroup} from 'shells/button-group/button-group';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {getUserFacingNoun} from 'shells/edition/get-user-facing-noun';
import {
    EditIcon,
    EnvelopeIcon,
    ReportsIcon,
    AudienceIcon,
    HalfAndHalfIcon,
    ChevronDownIcon,
} from 'shells/icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import {useManageSaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {SectionLabel} from 'shells/typography/section-label';

import type {CampaignType} from 'nutshell-graphql-types';
import type {
    EditionUpdateInput,
    EditionStubFragment as EditionStub,
    EditionFragment as Edition,
    PublishTemplate as PublishTemplateMutation,
    PublishTemplateVariables as PublishTemplateMutationVariables,
} from 'nutshell-graphql-types';

import {useGetSessionUser} from '../../../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../../../layout';
import type {ReportURLParams} from '../../../reports/report-types';

import {BeeEditorEmailBuilder} from '../../bee-editor/email-builder/email-builder-editor';
import PUBLISH_TEMPLATE from '../../graphql-wrappers/template/graphql/mutations/publish-template.graphql';
import {MarketingStatsBanner} from '../../marketing-stats-banner/marketing-stats-banner';

import {
    ABTestDetails,
    EditionVersionPicker,
    CompareContentBar,
} from '../a-b-testing/edition-content';
import {useToggleComparison} from '../a-b-testing/graphql/hooks';
import {getEditionStatusBarInfo} from '../edition-helpers';
import {getBeeEditorStyle} from '../edition-page';
import {EditionsDetailsForm} from '../editions-details/editions-details-form';
import {EditionEngagement} from '../engagement/edition-engagement';
import {PrepareToSendModal} from '../prepare-to-send-modal/prepare-to-send-modal';
import {RescheduleModal} from '../prepare-to-send-modal/reschedule-modal';
import {EditionRecipientEngagementFilter} from '../recipient-list/edition-recipient-engagement-filter';
import {EditionRecipientList} from '../recipient-list/edition-recipient-list';
import {
    adjustFiltersForComparisonEdition,
    editionFieldFilterFunction,
} from '../recipient-list/helpers';
import {SendTestModal} from '../send-test-modal/send-test-modal';

import {PublishTemplateButton} from './publish-template-button';
import {ShareUrlPopover} from './share-url-popover';

import styles from './edition-content-page.module.css';

export const SENT_STATUSES = [
    'SENDING',
    'SENT',
    'HALTED',
    'IN_REVIEW',
    'COMPARISON_SENDING',
    'COMPARISON_WAITING',
] as const;

export type ModalComponent = React.ComponentType<{
    isOpen: boolean;
    onClose: () => void;
}>;

type Props = ContextRouter & {
    onRemoveAllFilters: () => void;
    edition: Edition | null | undefined;
    editionStub: EditionStub | null | undefined;
    activeTab: string;
    isLoading: boolean;
    onSendEmail: (toEmailAddress: string[]) => Promise<any>;
    onChange: (editionId: string, beeBodyJson: string) => Promise<any>;
    onEditionSave: (saveData: EditionUpdateInput) => Promise<any>;
    isSaving: boolean;
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onParamPush: (arg1: any, arg2: boolean) => any;
    urlParams: ReportURLParams<'contacts'>;
    onSetSelectedEditorTab: (arg1: string) => void;
    campaignType: CampaignType;
    generatePath: (arg1: string) => string;
    isPreparingToSend: boolean;
    isScheduling: boolean;
    isRescheduling: boolean;
    setIsPreparingToSend: (arg1: boolean) => void;
    setIsScheduling: (arg1: boolean) => void;
    setIsRescheduling: (arg1: boolean) => void;
    setOnboardingModalComponent: (component?: React.ReactNode | null | undefined) => void;
};

export function EditionContentPage(props: Props) {
    const [shouldShowConfirmation, setShouldShowConfirmation] = React.useState<boolean>(false);
    const [shouldShowEmailEditor, setShouldShowEmailEditor] = React.useState<boolean>(false);
    const [isSendingTestEmail, setIsSendingTestEmail] = React.useState<boolean>(false);
    const [testEmailErrorMessage, setTestEmailErrorMessage] = React.useState<
        string | null | undefined
    >(null);
    const [sendToEmailAddress, setSendToEmailAddress] = React.useState<string[]>([]);
    const [isSavingEditionDetails, setIsSavingEditionDetails] = React.useState<boolean>(false);
    const [shouldShowShareUrlPopover, setShouldShowShareUrlPopover] =
        React.useState<boolean>(false);

    const shareUrlPopoverRef = React.useRef();
    const {toastState} = useManageSaveState(isSavingEditionDetails, 1000);

    const {user} = useGetSessionUser();

    const {activeTab, isLoading, edition, editionStub, onSetSelectedEditorTab} = props;

    const {toggleComparison, isLoading: isToggleLoading} = useToggleComparison();

    const editionData = edition || editionStub;

    const isABTesting = Boolean(editionData && editionData.isABTesting);

    const isEditionSent = edition && SENT_STATUSES.includes(edition.status);
    const isEditionScheduled = edition && edition.status === 'SCHEDULED';
    const isNewsletter = props.campaignType === 'NEWSLETTER';
    const isTemplate = props.campaignType === 'TEMPLATE';
    const editionTitle = getUserFacingNoun(props.campaignType);

    let preventTestTooltipText = null;
    if (edition && !edition.emailTemplate)
        preventTestTooltipText = 'Please add content to the email first';

    const [selectedEdition, setSelectedEdition] = React.useState(edition);
    const [isComparingContent, setIsComparingContent] = React.useState<boolean>(
        (edition && edition.comparison && edition.comparison.shouldCompareContent) || false
    );
    // Need to keep track of the selected edition ID so we can properly set the selected edition in the BEE editor
    const selectedEditionIdRef = React.useRef(selectedEdition && selectedEdition.id);
    // Get the current version from the URL
    const urlParams = new URLSearchParams(props.history.location.search);
    const version = urlParams.get('version');

    const adjustedFilters: FilterObject[] =
        edition && edition.comparisonEdition
            ? adjustFiltersForComparisonEdition(
                  props.activeFilters,
                  edition.comparisonEdition.id,
                  props.urlParams.version
              )
            : props.activeFilters;

    React.useEffect(() => {
        setIsComparingContent(
            (edition && edition.comparison && edition.comparison.shouldCompareContent) || false
        );
        // Set the selected edition based on the version in the URL
        if (version === 'B') {
            setSelectedEdition(edition && edition.comparisonEdition);
        } else {
            setSelectedEdition(edition);
        }
        selectedEditionIdRef.current = selectedEdition && selectedEdition.id;
    }, [edition, version, selectedEdition]);

    let detailsType = 'Broadcast';
    if (props.campaignType === 'NEWSLETTER') {
        detailsType = 'Edition';
    } else if (props.campaignType === 'TEMPLATE') {
        detailsType = 'Template';
    }

    const links = edition && edition.emailTemplate ? edition.emailTemplate.links : [];
    const urls = links
        ? links
              // @ts-expect-error
              .filter((link): boolean => Boolean(link.url))
              // @ts-expect-error
              .map((link) => {
                  return link.url;
              })
        : [];

    const comparisonLinks =
        edition && edition.comparisonEdition && edition.comparisonEdition.emailTemplate
            ? edition.comparisonEdition.emailTemplate.links
            : [];

    const comparisonUrls = comparisonLinks
        ? comparisonLinks
              // @ts-expect-error
              .filter((link): boolean => Boolean(link.url))
              // @ts-expect-error
              .map((link) => {
                  return link.url;
              })
        : [];

    const tabOptions = [
        {
            id: 'details',
            label: 'Details',
            icon: EnvelopeIcon,
            href: props.generatePath('details'),
            component: edition ? (
                <div className={styles['right-container']}>
                    <SectionLabel>
                        <SavedSettingToast state={toastState}>
                            {detailsType} Details
                        </SavedSettingToast>
                    </SectionLabel>
                    <EditionsDetailsForm
                        edition={edition}
                        isABTesting={isABTesting}
                        onEditionSave={(saveData) => {
                            setIsSavingEditionDetails(true);

                            return props.onEditionSave(saveData).then(() => {
                                setIsSavingEditionDetails(false);
                            });
                        }}
                        setIsPreparingToSend={props.setIsPreparingToSend}
                        onSetSelectedEditorTab={onSetSelectedEditorTab}
                        onIsSavingDetailsChange={setIsSavingEditionDetails}
                    />
                </div>
            ) : (
                <></>
            ),
        },
    ];
    if (props.campaignType !== 'TEMPLATE') {
        tabOptions.push({
            id: 'engagement',
            label: 'Engagement',
            icon: ReportsIcon,
            href: props.generatePath('engagement'),
            component: edition ? (
                <EditionEngagement
                    // @ts-expect-error
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    onParamPush={props.onParamPush}
                    urlParams={props.urlParams}
                    edition={edition}
                />
            ) : (
                <></>
            ),
        });
    }

    if (
        NutClientConfig.hasABTesting &&
        edition &&
        !isTemplate &&
        ((!isEditionSent && !isEditionScheduled) || isABTesting)
    ) {
        tabOptions.splice(1, 0, {
            id: 'a-b-test',
            label: 'A/B test',
            icon: HalfAndHalfIcon,
            href: props.generatePath('a-b-test'),
            component: (
                <ABTestDetails
                    edition={edition}
                    isABTesting={isABTesting}
                    setShouldShowConfirmation={setShouldShowConfirmation}
                    isEditionSent={isEditionSent}
                />
            ),
        });
    }

    if (
        (!isEditionSent && !NutClientConfig.hasNewAudienceManagementNUT14721) ||
        (!isEditionSent && NutClientConfig.hasNewAudienceManagementNUT14721 && !isEditionScheduled)
    ) {
        // @ts-expect-error
        tabOptions.unshift({
            id: 'editor',
            label: 'Editor',
            icon: EditIcon,
            href: props.generatePath('editor'),
        });
    }

    // Currently only showing recipient list after the edition is sent
    if (edition && (edition.status === 'SENT' || edition.status === 'HALTED')) {
        tabOptions.push({
            id: 'recipients',
            label: 'Recipients',
            icon: AudienceIcon,
            href: props.generatePath('recipients'),
            component: (
                <EditionRecipientList
                    activeFilters={props.activeFilters}
                    onParamChange={props.onParamChange}
                    urlParams={props.urlParams}
                    onRemoveAllFilters={props.onRemoveAllFilters}
                    edition={edition}
                    campaignType={props.campaignType}
                    graduatedFilters={
                        <EditionRecipientEngagementFilter
                            editionId={edition.id}
                            comparisonEditionId={
                                edition.comparisonEdition && edition.comparisonEdition.id
                            }
                            comparisonWinnerEditionId={
                                edition.comparison &&
                                edition.comparison.winnerEdition &&
                                edition.comparison.winnerEdition.id
                            }
                            // Note, this is the prop, not the filtered filters.
                            // We want to reflect the "default" filter here
                            filters={adjustedFilters}
                            onParamChange={props.onParamChange}
                            urlParams={props.urlParams}
                            urls={urls}
                            comparisonUrls={comparisonUrls}
                            createdTime={edition.createdTime}
                        />
                    }
                    fieldFilterFunction={editionFieldFilterFunction}
                />
            ),
        });
    }

    // @ts-expect-error
    const onToggleCompareContent = (isPreviouslyComparingContent) => {
        // Need to select version A if no longer comparing
        if (isPreviouslyComparingContent && edition) {
            setSelectedEdition(edition);
            props.onParamChange({version: 'A'}, true);
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        editionData
            ? props.onEditionSave({
                  editionId: editionData.id,
                  comparison: {
                      shouldCompareContent: !isPreviouslyComparingContent,
                  },
              })
            : undefined;

        setIsComparingContent(!isPreviouslyComparingContent);
    };

    // @ts-expect-error
    const onEditionPickerChange = (value) => {
        if (edition) {
            if (value.value !== edition.id) {
                setSelectedEdition(edition.comparisonEdition);
                props.onParamChange({version: 'B'}, true);
            } else {
                setSelectedEdition(edition);
                props.onParamChange({version: 'A'}, true);
            }
        }
    };

    const handleSchedule = () => {
        if (!edition) {
            return;
        }

        if (NutClientConfig.hasNewAudienceManagementNUT14721) {
            if (edition.status === 'SCHEDULED') {
                props.setIsRescheduling(true);
            } else {
                props.setIsScheduling(true);
                onSetSelectedEditorTab('prepare');
            }
        }

        if (!NutClientConfig.hasNewAudienceManagementNUT14721) {
            if (edition.status === 'SCHEDULED') {
                props.setIsRescheduling(true);
            } else {
                props.setIsScheduling(true);
                props.setIsPreparingToSend(true);
            }
        }
    };

    const [publishTemplate] = useMutation<
        PublishTemplateMutation,
        PublishTemplateMutationVariables
    >(PUBLISH_TEMPLATE);

    const isOwner = Boolean(
        user &&
            edition &&
            edition.campaign &&
            edition.campaign.owner &&
            edition.campaign.owner.id === user.id
    );

    return (
        <PageContainer
            title={getTitle(editionData, props.campaignType)}
            noPadding={
                activeTab === 'editor' || activeTab === 'a-b-test' || activeTab === 'recipients'
            }
            whiteBackground={activeTab === 'a-b-test'}
            pageTitleBar={
                <PageTitleBar
                    title={getTitle(editionData, props.campaignType)}
                    subheaderPrimaryText={
                        isNewsletter ? 'Edit your edition' : 'Edit your broadcast'
                    }
                    noMaxWidth={true}
                    breadcrumbs={getBreadcrumbs(editionData)}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) =>
                            editionData
                                ? props.onEditionSave({
                                      editionId: editionData.id,
                                      name: newValue ? newValue : '',
                                  })
                                : undefined,
                        placeholder: isNewsletter ? 'Name this edition' : 'Name this broadcast',
                    }}
                    buttons={
                        !isEditionSent ? (
                            <ButtonGroup>
                                {isABTesting ? (
                                    <>
                                        <Button
                                            disabled={isLoading || !edition}
                                            variant={
                                                shouldShowShareUrlPopover ? 'primary' : 'secondary'
                                            }
                                            size='big'
                                            onClick={() =>
                                                setShouldShowShareUrlPopover(
                                                    !shouldShowShareUrlPopover
                                                )
                                            }
                                            getButtonRef={shareUrlPopoverRef}
                                        >
                                            <StackLegacy spacing={6} isHorizontal={true}>
                                                Share URL
                                                <ChevronDownIcon />
                                            </StackLegacy>
                                        </Button>
                                        {shouldShowShareUrlPopover ? (
                                            <ShareUrlPopover
                                                anchor={shareUrlPopoverRef.current}
                                                versionA={edition ? edition.viewInBrowserLink : ''}
                                                versionB={
                                                    edition &&
                                                    edition.comparison &&
                                                    edition.comparisonEdition &&
                                                    edition.comparison.shouldCompareContent
                                                        ? edition.comparisonEdition
                                                              .viewInBrowserLink
                                                        : ''
                                                }
                                                onBlur={() => setShouldShowShareUrlPopover(false)}
                                            />
                                        ) : null}
                                    </>
                                ) : undefined}
                                <Button
                                    disabled={isLoading || !edition}
                                    variant='secondary'
                                    size='big'
                                    data-tip={preventTestTooltipText}
                                    onClick={() => {
                                        // HACK: can't show a tooltip when the button is disabled, so we need to faux disable it
                                        if (preventTestTooltipText) return;
                                        setShouldShowEmailEditor(true);
                                    }}
                                >
                                    {isABTesting ? 'Send test emails' : 'Send a test email'}
                                </Button>
                                <>
                                    {canSendEdition(edition, props.campaignType) ? (
                                        <Button
                                            disabled={isLoading || !edition}
                                            variant='primary'
                                            size='big'
                                            onClick={() => {
                                                if (
                                                    NutClientConfig.hasNewAudienceManagementNUT14721
                                                ) {
                                                    onSetSelectedEditorTab('prepare');
                                                } else {
                                                    props.setIsScheduling(false);
                                                    props.setIsPreparingToSend(true);
                                                }
                                            }}
                                        >
                                            Prepare to send
                                        </Button>
                                    ) : undefined}
                                </>
                                <>
                                    {isTemplate && edition ? (
                                        <PublishTemplateButton
                                            campaign={edition.campaign}
                                            onPublish={(campaignId) => {
                                                return publishTemplate({
                                                    variables: {
                                                        input: {
                                                            template: {
                                                                id: campaignId,
                                                            },
                                                        },
                                                    },
                                                });
                                            }}
                                        />
                                    ) : undefined}
                                </>
                            </ButtonGroup>
                        ) : undefined
                    }
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={
                        edition
                            ? getEditionStatusBarInfo(
                                  edition,
                                  Boolean(user && user.permissions.canAccessFullCampaigns),
                                  handleSchedule,
                                  props.isSaving,
                                  isOwner
                              )
                            : undefined
                    }
                    tabs={{
                        selectedTab: activeTab || 'editor',
                        options: tabOptions,
                    }}
                />
            }
        >
            <MarketingStatsBanner />
            {/* This might look like its a mess of a rats nest, but that's mostly
            because it is. We need to always show the BEE editor, and just
            change the passed props becuase otherwise it forces the BEE editor
            to reload each time the user switches tabs. To get around this, we
            show the BEE editor constantly and just change its size and display
            value. */}
            <div className='routed-report-content'>
                {props.isLoading ? <LoadingPage /> : undefined}
                {selectedEdition && edition ? (
                    <div className='full-height'>
                        {activeTab === 'editor' && isABTesting ? (
                            <CompareContentBar
                                isComparingContent={isComparingContent}
                                edition={edition}
                                onToggleCompareContent={onToggleCompareContent}
                                selectedVersionId={selectedEdition.id}
                                onEditionPickerChange={onEditionPickerChange}
                            />
                        ) : null}
                        <div
                            className='flex'
                            style={{height: getMainContentHeight(activeTab, isABTesting)}}
                        >
                            {/* @ts-expect-error */}
                            <div style={getBeeEditorStyle(activeTab)}>
                                {activeTab === 'details' ? (
                                    <>
                                        <SectionLabel>Preview</SectionLabel>
                                        {isComparingContent ? (
                                            <EditionVersionPicker
                                                edition={edition}
                                                styles={{position: 'absolute', top: 48}}
                                                selectedVersionId={selectedEdition.id}
                                                onChange={onEditionPickerChange}
                                            />
                                        ) : null}
                                    </>
                                ) : undefined}
                                <BeeEditorEmailBuilder
                                    editionId={selectedEdition.id}
                                    onChange={(json) => {
                                        try {
                                            return props.onChange(
                                                selectedEditionIdRef.current || '',
                                                json
                                            );
                                        } catch {
                                            // @ts-expect-error
                                            window.trackJs.track(
                                                'Edition page changes failed to save'
                                            );
                                        }
                                    }}
                                    marketingEmailTemplate={
                                        selectedEdition && selectedEdition.emailTemplate
                                            ? selectedEdition.emailTemplate.beeBodyJson
                                            : undefined
                                    }
                                    testEmailToAddress={
                                        activeTab === 'editor' ? sendToEmailAddress : undefined
                                    }
                                    isPreview={activeTab !== 'editor'}
                                    editionPreviewUrl={selectedEdition.viewInBrowserLink}
                                />
                            </div>
                            {/* @ts-expect-error */}
                            <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
                        </div>
                    </div>
                ) : undefined}
            </div>
            <SendTestModal
                isOpen={shouldShowEmailEditor}
                onClose={() => {
                    setShouldShowEmailEditor(false);
                    setIsSendingTestEmail(false);
                    setSendToEmailAddress([]);
                }}
                // @ts-expect-error
                onSend={(emailAddresses) => {
                    setIsSendingTestEmail(true);
                    props
                        .onSendEmail(emailAddresses)
                        .then(() => {
                            setIsSendingTestEmail(false);
                        })
                        // @ts-expect-error
                        .catch((err) => {
                            setIsSendingTestEmail(false);
                            if (err && err.message) {
                                setTestEmailErrorMessage(safelyGetGraphQLErrorMessage(err));
                            } else {
                                setTestEmailErrorMessage(
                                    'Sorry, something went wrong. Unable to send a test'
                                );
                            }
                        });
                }}
                isSending={isSendingTestEmail}
                testEmailErrorMessage={testEmailErrorMessage}
                onHideTestEmailErrorMessage={() => setTestEmailErrorMessage(null)}
                isABTesting={isABTesting}
            />
            {edition && !NutClientConfig.hasNewAudienceManagementNUT14721 ? (
                <>
                    {props.isPreparingToSend ? (
                        <PrepareToSendModal
                            isOpen={props.isPreparingToSend}
                            onClose={(
                                editorTab?: string | null,
                                onboardingComponent?: ModalComponent | null
                            ) => {
                                props.setIsPreparingToSend(false);
                                if (editorTab) {
                                    onSetSelectedEditorTab(editorTab);
                                }

                                // Occasionally the primary action from the PrepareToSend modal
                                // is a "Show me how", in which we'll pop an onboarding modal
                                if (onboardingComponent) {
                                    const OnboardingModal = onboardingComponent;
                                    // Wait a second for the user to get oriented with a previously
                                    // closed modal
                                    setTimeout(() => {
                                        props.setOnboardingModalComponent(
                                            <OnboardingModal
                                                isOpen={true}
                                                onClose={() =>
                                                    props.setOnboardingModalComponent(null)
                                                }
                                            />
                                        );
                                    }, 1000);
                                }
                            }}
                            onSendSuccess={() => onSetSelectedEditorTab('engagement')}
                            edition={edition}
                            isScheduling={props.isScheduling}
                            // @ts-expect-error
                            setIsScheduling={props.setIsScheduling}
                            type={props.campaignType}
                            isEditionLoading={props.isLoading || props.isSaving}
                        />
                    ) : undefined}
                </>
            ) : undefined}
            {edition ? (
                <RescheduleModal
                    isOpen={props.isRescheduling}
                    onClose={() => {
                        props.setIsRescheduling(false);
                    }}
                    edition={edition}
                    type={props.campaignType}
                />
            ) : undefined}
            {shouldShowConfirmation && NutClientConfig.hasABTesting ? (
                <ConfirmationDialog
                    title={`Turn this ${editionTitle} into an A/B test`}
                    message={`You’re about to enable A/B testing for this ${editionTitle}. Once enabled, you can’t revert back to a standard ${editionTitle}.`}
                    confirmText='Enable A/B test'
                    isConfirmDestructive={false}
                    autoFocusCancelButton={false}
                    onCancel={() => setShouldShowConfirmation(false)}
                    isSaving={isToggleLoading}
                    onConfirm={() => {
                        toggleComparison({
                            editionId: editionData ? editionData.id : '',
                            shouldBeComparison: true,
                        }).then(() => {
                            setShouldShowConfirmation(false);
                        });
                    }}
                />
            ) : undefined}
        </PageContainer>
    );
}

function getTitle(
    editionData: Edition | null | undefined | EditionStub,
    campaignType: CampaignType
): string {
    if (editionData) {
        return editionData.name;
    }

    return campaignType === 'NEWSLETTER' ? 'Edition' : 'Broadcast';
}

// We have an overflow: hidden declaration on the header, and we need to
// properly size our BEE container. They have some inner-iframe browser
// scroll behavior happening that fights with our layout. This comment is
// likely 85% accurate.
function getMainContentHeight(activeTab: string, isABTesting: boolean) {
    return activeTab === 'editor' && isABTesting ? 'calc(100% - 67px)' : '100%';
}

function getBreadcrumbs(editionData: Edition | null | undefined | EditionStub) {
    switch (editionData && editionData.campaign ? editionData.campaign.type : 'BROADCAST') {
        case 'AI_STARTER_TEMPLATE':
        case 'NEWSLETTER':
            return [
                {
                    to:
                        editionData && editionData.campaign.deletedTime
                            ? '/email-marketing/newsletters/archived'
                            : '/email-marketing/newsletters',
                    text: 'Newsletters',
                },
                {
                    to: editionData
                        ? `/email-marketing/newsletters/${editionData.campaign.id}`
                        : '#',
                    text: editionData ? editionData.campaign.name : 'Campaign',
                },
            ];
        case 'TEMPLATE':
            return [
                {
                    to: '/email-marketing/templates',
                    text: 'Templates',
                },
            ];
        default:
            return [
                {
                    to: '/email-marketing/broadcasts',
                    text: 'Broadcasts',
                },
            ];
    }
}

function canSendEdition(
    edition: Edition | null | undefined | EditionStub,
    campaignType: CampaignType
): boolean {
    if (campaignType === 'TEMPLATE') {
        return false;
    }

    if (edition && edition.status === 'SCHEDULED') {
        return false;
    }

    if (edition && edition.deletedTime) {
        return false;
    }

    return true;
}
